import { useCallback, useEffect, useMemo, useState } from 'react';
import { Input } from 'semantic-ui-react';

import {
  autoFixCourseName,
  suggestCourseName,
  suggestNameFromSessions,
  validateCourseName,
  type ScheduleSuggestion,
  type SessionLike,
} from '../utils/courseName';

import styles from './CourseNameField.module.css';

const TIDIED_FLASH_MS = 6000;

const prettySlot = (key: string): string => key.replace('_', ' ');

const listSlots = (keys: string[]): string =>
  keys.map(prettySlot).join(keys.length > 2 ? ', ' : ' and ');

const describeSchedule = (suggestion: ScheduleSuggestion): string => {
  const { additions, removals } = suggestion;
  if (additions.length > 0 && removals.length > 0) {
    return `Sessions run ${listSlots(additions)}, not ${listSlots(removals)}.`;
  }
  if (additions.length > 0) return `Sessions also run ${listSlots(additions)}.`;
  return `No sessions left on ${listSlots(removals)}.`;
};

export type CourseNameHelp = {
  valid: boolean;
  errors: string[];
  /** A better spelling of the same name, or null. May itself still be invalid. */
  formatFix: string | null;
  /** Whether applying `formatFix` would actually produce a valid name. */
  formatFixIsValid: boolean;
  /** Only ever set once the name is valid and the course has enough sessions left. */
  schedule: ScheduleSuggestion | null;
};

/**
 * Everything the UI needs to know about a typed course name. Callers that draw their own input
 * (the TimeTable inline editor) use this directly; `CourseNameField` wraps it up with an Input.
 */
export const useCourseNameHelp = (value: string, sessions?: SessionLike[]): CourseNameHelp =>
  useMemo(() => {
    const { valid, errors } = validateCourseName(value);
    const formatFix = valid ? null : autoFixCourseName(value) ?? suggestCourseName(value);

    return {
      valid,
      errors,
      formatFix,
      formatFixIsValid: formatFix != null && validateCourseName(formatFix).valid,
      schedule: valid && sessions != null ? suggestNameFromSessions(value, sessions) : null,
    };
  }, [value, sessions]);

/**
 * The Tier A fix, applied silently. Returns the tidied name so the caller can flash it, or null
 * if there was nothing safe to do. Only ever called on blur — rewriting mid-word would fight
 * the cursor.
 */
export const tidyCourseNameOnBlur = (
  value: string,
  onChange: (name: string) => void,
): string | null => {
  const fixed = autoFixCourseName(value);
  if (fixed == null) return null;
  onChange(fixed);
  return fixed;
};

/**
 * Holds the "Tidied to …" note for a few seconds. A silent fix must still be visible, but it
 * should not sit there for the rest of the session.
 */
export const useTidiedFlash = (): [string | null, (name: string | null) => void] => {
  const [tidiedTo, setTidiedTo] = useState<string | null>(null);

  useEffect(() => {
    if (tidiedTo == null) return undefined;
    const timer = setTimeout(() => setTidiedTo(null), TIDIED_FLASH_MS);
    return () => clearTimeout(timer);
  }, [tidiedTo]);

  return [tidiedTo, setTidiedTo];
};

export const CourseNameHints = ({
  value,
  sessions,
  onApply,
  tidiedTo,
}: {
  value: string;
  sessions?: SessionLike[];
  onApply: (name: string) => void;
  tidiedTo?: string | null;
}) => {
  const { valid, errors, formatFix, formatFixIsValid, schedule } = useCourseNameHelp(value, sessions);

  if (value.trim() === '') return null;

  const showTidied = tidiedTo != null && tidiedTo === value;

  return (
    <div className={styles.hints}>
      {showTidied && (
        <div className={styles.tidied}>
          Tidied to <span className={styles.name}>{tidiedTo}</span>
        </div>
      )}

      {!valid && formatFix != null && (
        <div className={styles.suggestion}>
          <span>
            Did you mean <span className={styles.name}>{formatFix}</span>?
          </span>
          <button
            type="button"
            className={styles.useThis}
            // Keep focus on the input: without this the mousedown blurs it, the blur handler
            // re-renders the hint away, and the click never reaches this button.
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onApply(formatFix)}
          >
            Use this
          </button>
          {!formatFixIsValid && (
            <span className={styles.suggestionReason}>— still needs a bit more</span>
          )}
        </div>
      )}

      {!valid && errors.length > 0 && (
        <ul className={styles.errors}>
          {errors.map((error) => (
            <li key={error}>{error}</li>
          ))}
        </ul>
      )}

      {valid && schedule != null && (
        <div className={styles.suggestion}>
          <span>
            {describeSchedule(schedule)} Rename to{' '}
            <span className={styles.name}>{schedule.suggestedName}</span>?
          </span>
          <button
            type="button"
            className={styles.useThis}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onApply(schedule.suggestedName)}
          >
            Use this
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * A class-name input that tidies unambiguous mistakes silently on blur and offers the rest as
 * suggestions. Pass `sessions` to also have the day/time half of the name checked against the
 * course's remaining sessions.
 */
export const CourseNameField = ({
  value,
  onChange,
  onCommit,
  sessions,
  placeholder = 'e.g. SG-Bengali-L1-Mon_10.00+Thu_10.00',
  fluid = true,
  disabled = false,
  autoFocus = false,
}: {
  value: string;
  onChange: (name: string) => void;
  /**
   * Called when a name is *settled* rather than merely typed — the user accepted a suggestion,
   * or blur tidied the name. Callers that save on a throttle should save here directly:
   * accepting a suggestion is a deliberate act and should not wait on (or be filtered by) the
   * as-you-type path.
   */
  onCommit?: (name: string) => void;
  sessions?: SessionLike[];
  placeholder?: string;
  fluid?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
}) => {
  const [tidiedTo, setTidiedTo] = useTidiedFlash();

  const commit = useCallback(
    (name: string) => {
      onChange(name);
      onCommit?.(name);
    },
    [onChange, onCommit],
  );

  const handleChange = useCallback(
    (_: unknown, { value: next }: { value: string }) => {
      setTidiedTo(null);
      onChange(next);
    },
    [onChange, setTidiedTo],
  );

  const handleBlur = useCallback(() => {
    setTidiedTo(tidyCourseNameOnBlur(value, commit));
  }, [value, commit, setTidiedTo]);

  const handleApply = useCallback(
    (name: string) => {
      setTidiedTo(null);
      commit(name);
    },
    [commit, setTidiedTo],
  );

  return (
    <div className={styles.wrapper}>
      <Input
        fluid={fluid}
        disabled={disabled}
        autoFocus={autoFocus}
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
      />
      <CourseNameHints value={value} sessions={sessions} onApply={handleApply} tidiedTo={tidiedTo} />
    </div>
  );
};
